import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// The prepayments page: plan and payment-method selection, the two gating buttons, and the charge
// POST. Card add/replace/delete belongs to payment_method_controller.js, which both owner sections
// mount directly.
//
// Mounted ONCE, on a single wrapping element around both panels. Every selector here is
// page-global, so a second mount would double-fire every listener.
//
// The current-payment-method click stays a jQuery delegated binding, paired with the plan-radio
// click on the same jQuery namespace: payment_method_controller.js auto-selects a newly added card
// with a jQuery synthetic click, and that markup (payments_helper.rb's payment_method_component
// partial) is shared by several other pages, not owned by this cluster's view, so it cannot take a
// data-action here.
//
// planField/paymentMethodField/customField's change binding stays jQuery too, for a different
// reason: every write to them goes through a jQuery `.trigger("change")` (either directly, or via
// payment_method_controller.js's synthetic click reaching the delegated handler below), and jQuery
// only invokes a *native* event for types with a native method on the element (click, focus, blur,
// submit, ...). "change" has none, so `.trigger("change")` never reaches `dispatchEvent` -- a native
// addEventListener on these fields would simply never fire.
//
// invoiceButton stays jQuery (and page-global, not a Target) for two reasons: it drives
// `.tooltip("enable"/"disable")`, a Bootstrap jQuery plugin call, and its markup renders in the
// invoiceable-organization toast, a sibling of this controller's own element rather than a
// descendant of it.
export default class extends Controller {
  static get targets() {
    return [
      "form", "planField", "paymentMethodField", "customField", "payButton", "creditsTotal",
      "cashTotal", "planRadio", "customRadio", "customAmountField"
    ];
  }

  connect() {
    this.$planField = $(this.planFieldTarget);
    this.$paymentMethodField = $(this.paymentMethodFieldTarget);
    this.$customField = $(this.customFieldTarget);
    this.$invoiceButton = $("#invoice-btn");

    this.$customField.numeric({ negative: false, decimal: false });

    this.onPlanRadioClick = (event) => {
      const target = event.currentTarget;

      this.$planField.val(target.value);
      this.$planField.trigger("change");
      // Dead legacy id -- no view in this cluster renders it, so this has always been a no-op;
      // preserved rather than assumed safe to drop.
      const legacyPlanField = this.element.querySelector("#charge_prepayment_prepayment_plan_id");
      if (legacyPlanField) { legacyPlanField.value = target.value; }
      this.customFieldTarget.value = "";
      this.$customField.trigger("change");

      this.creditsTotalTarget.textContent = target.dataset.credit;
      this.cashTotalTarget.textContent = target.dataset.cash;
    };
    $(this.element).on("click.prepayment", ".prepayment input[type=radio]", this.onPlanRadioClick);

    this.enablePayButton = () => this._enablePayButton();
    this.enableInvoiceButton = () => this._enableInvoiceButton();

    this.$customField.on("change.prepayment", this.enablePayButton);
    this.$planField.on("change.prepayment", this.enablePayButton);
    this.$paymentMethodField.on("change.prepayment", this.enablePayButton);

    this.$customField.on("keyup.prepayment change.prepayment", this.enableInvoiceButton);
    this.$planField.on("change.prepayment", this.enableInvoiceButton);

    this.onPaymentMethodClick = (event) => {
      if (event.target.closest("button")) { return; }

      const target = event.currentTarget;
      const id = target.dataset.id;

      this.paymentMethodFieldTarget.value = id;
      this.$paymentMethodField.trigger("change");

      this.element.querySelectorAll(".payment-method-widget").forEach((el) => el.classList.remove("active"));
      target.parentElement.classList.add("active");
    };
    $(this.element).on("click.prepayment", ".current-payment-method", this.onPaymentMethodClick);

    // If a prepayment option is preselected on page load, make sure the hidden field gets that
    // value.
    const checkedPlanRadio = this.planRadioTargets.find((radio) => radio.checked);
    if (!this.planFieldTarget.value && checkedPlanRadio) {
      this.planFieldTarget.value = checkedPlanRadio.value;
      this.$planField.trigger("change");
    }
    this._enableInvoiceButton();
  }

  disconnect() {
    $(this.element).off("click.prepayment", ".prepayment input[type=radio]", this.onPlanRadioClick);
    $(this.element).off("click.prepayment", ".current-payment-method", this.onPaymentMethodClick);
    if (this.$customField) { this.$customField.off(".prepayment"); }
    if (this.$planField) { this.$planField.off(".prepayment"); }
    if (this.$paymentMethodField) { this.$paymentMethodField.off(".prepayment"); }
  }

  // Focusing the custom amount field should select the custom radio button.
  selectCustomRadio() {
    this.customRadioTarget.click();
  }

  updateCustomTotals(event) {
    if (event.currentTarget.value.length === 0) {
      this.cashTotalTarget.textContent = "-";
      this.creditsTotalTarget.textContent = "-";
      return;
    }

    this.creditsTotalTarget.textContent = event.currentTarget.value;
    this.cashTotalTarget.textContent = `$${parseInt(event.currentTarget.value).toLocaleString("en-US")}`;
  }

  // Mirrors PrepaymentPlan.plan_or_new_plan_for_amount, which is the real check: a whole number
  // of dollars, at least $1.
  _validCustomAmount() {
    return parseInt(this.customFieldTarget.value, 10) >= 1;
  }

  _enablePayButton() {
    this.payButtonTarget.classList.add("btn-disabled");

    if (this.planFieldTarget.value.length === 0) { return; }
    if (this.planFieldTarget.value === "0" && !this._validCustomAmount()) { return; }
    if (this.paymentMethodFieldTarget.value.length === 0) { return; }

    this.payButtonTarget.classList.remove("btn-disabled");
  }

  _enableInvoiceButton() {
    this.$invoiceButton.addClass("btn-disabled");
    this.$invoiceButton.parents(".tooltip-container").tooltip("enable");

    if (this.planFieldTarget.value.length === 0) { return; }
    if (this.planFieldTarget.value === "0" && !this._validCustomAmount()) { return; }

    this.$invoiceButton.removeClass("btn-disabled");
    this.$invoiceButton.parents(".tooltip-container").tooltip("disable");
  }

  // A real click on #complete-payment, so this is a plain data-action (Step 6c) rather than a
  // jQuery binding.
  pay(event) {
    event.preventDefault();
    this.payButtonTarget.disabled = true;

    if (this.planFieldTarget.value.length === 0) {
      window.ltAlert({ message: "You must select a credit option.", type: "toast-error", duration: 2500 });
      this.payButtonTarget.disabled = false;
      return;
    }

    if (this.paymentMethodFieldTarget.value.length === 0) {
      window.ltAlert({ message: "You must select a payment method.", type: "toast-error", duration: 2500 });
      this.payButtonTarget.disabled = false;
      return;
    }

    if (this.customRadioTarget.checked) {
      if (this.customFieldTarget.value.length === 0) {
        window.ltAlert({ message: "You must enter a credit purchase amount.", type: "toast-error", duration: 2500 });
        this.payButtonTarget.disabled = false;
        return;
      } else if (this.customFieldTarget.value < 1) {
        window.ltAlert({ message: "You must enter a credit purchase amount of at least $1.", type: "toast-error", duration: 2500 });
        this.payButtonTarget.disabled = false;
        return;
      } else {
        this.customAmountFieldTarget.value = this.customFieldTarget.value;
      }
    }

    const params = {
      charge_prepayment: {
        prepayment_plan_id: this.planFieldTarget.value,
        payment_method_id: this.paymentMethodFieldTarget.value
      },
      prepayment_custom_amount: this.customAmountFieldTarget.value
    };

    // account_prepayments#create has only a format.json branch, so both the XHR and full Accept
    // headers are required, alongside Content-Type for the JSON body to parse at all.
    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*"
    });
    options.body = JSON.stringify(params);

    fetch(this.formTarget.action, options).then((response) => {
      return response.json().then((data) => ({ ok: response.ok, data }));
    }).then(({ ok, data }) => {
      if (!ok) { throw data; }

      window.dataLayer = window.dataLayer || [];
      if (data.ga4_events) { window.dataLayer.push(...data.ga4_events); }

      // S3: our own controller's JSON response, same origin as this page - asserted before
      // navigating per the S3 rule rather than trusting `data.redirect` outright. The failing
      // branch does not navigate.
      if (RequestHelper.isSameOriginUrl(data.redirect)) {
        window.location.href = data.redirect;
      } else {
        this.payButtonTarget.disabled = false;
        window.ltAlert({ message: "An error occurred while completing this action.", type: "toast-error", duration: 2500 });
      }
    }).catch((data) => {
      this.payButtonTarget.disabled = false;
      window.ltAlert({ message: data.message, type: "toast-error", duration: 2500 });
    });
  }
}
